import { useState } from 'react';
import { CheckCircle, Save } from 'lucide-react';
import { colors } from '../../styles/colors';
import { useToast } from '../../contexts/ToastContext';
import ActionButton from '../ActionButton';
import FileUploadZone from './FileUploadZone';
import ScanResultCard from './ScanResultCard';
import { normalizeDate } from '../../utils/formatters';
import * as pdfjsLib from 'pdfjs-dist';

// Configuration de pdfjs worker - utiliser fichier local depuis /public
pdfjsLib.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.mjs';

/**
 * Composant onglet Scan - Gère le scan OCR des factures/tickets
 */
function ScanTab() {
  const { showToast } = useToast();

  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [scanResults, setScanResults] = useState([]);
  const [expandedPages, setExpandedPages] = useState({});
  const [scanProgress, setScanProgress] = useState({ current: 0, total: 0 });

  // États pour l'édition
  const [editingPageIndex, setEditingPageIndex] = useState(null);
  const [editedData, setEditedData] = useState({});
  const [savingPageIndex, setSavingPageIndex] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});
  const [savingAll, setSavingAll] = useState(false);

  const handleFileSelect = (event) => {
    const selectedFile = event.target.files[0];
    if (!selectedFile) return;

    if (!selectedFile.type.includes('pdf') && !selectedFile.type.includes('image')) {
      showToast('Format de fichier non supporté', 'error');
      return;
    }

    setFile(selectedFile);
    setScanResults([]);
    setExpandedPages({});
    setScanProgress({ current: 0, total: 0 });

    if (selectedFile.type.includes('image')) {
      const reader = new FileReader();
      reader.onloadend = () => setPreview(reader.result);
      reader.readAsDataURL(selectedFile);
    } else {
      setPreview(null);
    }
  };

  const convertPdfPageToImage = async (pdfDoc, pageNum) => {
    const page = await pdfDoc.getPage(pageNum);
    const viewport = page.getViewport({ scale: 2.0 });

    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    canvas.height = viewport.height;
    canvas.width = viewport.width;

    await page.render({ canvasContext: context, viewport }).promise;

    const imageDataUrl = canvas.toDataURL('image/png');
    return imageDataUrl.split(',')[1];
  };

  const validateReceiptData = (data) => {
    const errors = {};

    if (!data.montantTTC || parseFloat(data.montantTTC) <= 0) {
      errors.montantTTC = 'Le montant TTC est obligatoire';
    }

    const ht = parseFloat(data.montantHT) || 0;
    const tva = parseFloat(data.montantTVA) || 0;
    const ttc = parseFloat(data.montantTTC) || 0;

    if (ht > 0 && tva > 0 && ttc > 0) {
      if (tva >= ht) errors.montantTVA = 'La TVA doit être inférieure au montant HT';
      if (tva >= ttc) errors.montantTVA = 'La TVA doit être inférieure au montant TTC';
      if (ht >= ttc) errors.montantHT = 'Le montant HT doit être inférieur au montant TTC';

      const calculatedTTC = ht + tva;
      const diff = Math.abs(calculatedTTC - ttc);
      if (diff > 0.02) {
        errors.calculation = `Incohérence: HT (${ht.toFixed(2)}) + TVA (${tva.toFixed(2)}) = ${calculatedTTC.toFixed(2)} ≠ TTC (${ttc.toFixed(2)})`;
      }
    }

    if (!data.date) {
      errors.date = 'La date est obligatoire';
    } else {
      const normalized = normalizeDate(data.date);
      if (!normalized.match(/^\d{4}-\d{2}-\d{2}$/)) {
        errors.date = 'Format de date invalide';
      } else {
        const dateParts = normalized.split('-');
        const year = parseInt(dateParts[0], 10);
        const month = parseInt(dateParts[1], 10);
        const day = parseInt(dateParts[2], 10);

        if (month < 1 || month > 12) {
          errors.date = 'Mois invalide (doit être entre 01 et 12)';
        } else if (day < 1 || day > 31) {
          errors.date = 'Jour invalide (doit être entre 01 et 31)';
        } else {
          const testDate = new Date(year, month - 1, day);
          if (testDate.getFullYear() !== year ||
              testDate.getMonth() !== month - 1 ||
              testDate.getDate() !== day) {
            errors.date = 'Date invalide (ex: 31 février n\'existe pas)';
          }
        }
      }
    }

    if (!data.fournisseur || data.fournisseur.trim() === '') {
      errors.fournisseur = 'Le fournisseur est obligatoire';
    }

    return { isValid: Object.keys(errors).length === 0, errors };
  };

  const scanSingleImage = async (imageBase64, pageNumber = 1) => {
    const response = await fetch('/.netlify/functions/scanReceipt', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ imageBase64, mimeType: 'image/png' }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Erreur lors du scan');
    }

    const result = await response.json();

    // Valider les données extraites immédiatement après le scan
    let validationResult = { isValid: true, errors: {} };
    if (result.success && result.data) {
      validationResult = validateReceiptData(result.data);
    }

    return {
      ...result,
      pageNumber,
      validationErrors: validationResult.errors,
      hasValidationErrors: !validationResult.isValid
    };
  };

  const handleScan = async () => {
    if (!file) {
      showToast('Veuillez sélectionner un fichier', 'error');
      return;
    }

    setScanning(true);
    setScanResults([]);

    try {
      if (file.type.includes('pdf')) {
        const arrayBuffer = await file.arrayBuffer();
        const pdfDoc = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
        const numPages = pdfDoc.numPages;

        const results = [];
        for (let pageNum = 1; pageNum <= numPages; pageNum++) {
          setScanProgress({ current: pageNum, total: numPages });
          const imageBase64 = await convertPdfPageToImage(pdfDoc, pageNum);
          const result = await scanSingleImage(imageBase64, pageNum);
          results.push(result);
        }

        setScanResults(results);
        setExpandedPages({ 0: true });

        // Compter les erreurs de validation
        const pagesWithErrors = results.filter(r => r.hasValidationErrors).length;
        if (pagesWithErrors > 0) {
          showToast(
            `${numPages} page${numPages > 1 ? 's' : ''} scannée${numPages > 1 ? 's' : ''} - ${pagesWithErrors} page${pagesWithErrors > 1 ? 's' : ''} avec erreurs de validation`,
            'warning'
          );
        } else {
          showToast(`${numPages} page${numPages > 1 ? 's' : ''} scannée${numPages > 1 ? 's' : ''} avec succès!`, 'success');
        }
      } else {
        const reader = new FileReader();
        const imageBase64 = await new Promise((resolve, reject) => {
          reader.onload = () => resolve(reader.result.split(',')[1]);
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });

        setScanProgress({ current: 1, total: 1 });
        const result = await scanSingleImage(imageBase64, 1);
        setScanResults([result]);
        setExpandedPages({ 0: true });

        if (result.hasValidationErrors) {
          showToast('Image scannée - données à corriger', 'warning');
        } else {
          showToast('Image scannée avec succès!', 'success');
        }
      }
    } catch (err) {
      console.error('Erreur lors du scan:', err);
      showToast(err.message || 'Erreur lors du scan du document', 'error');
    } finally {
      setScanning(false);
      setScanProgress({ current: 0, total: 0 });
    }
  };

  const togglePageExpansion = (index) => {
    setExpandedPages(prev => ({ ...prev, [index]: !prev[index] }));
  };

  const handleReset = () => {
    setFile(null);
    setPreview(null);
    setScanResults([]);
    setExpandedPages({});
    setScanProgress({ current: 0, total: 0 });
    setEditingPageIndex(null);
    setEditedData({});
    setValidationErrors({});
  };

  const handleEditPage = (pageIndex, pageData) => {
    setEditingPageIndex(pageIndex);
    setEditedData({
      nom: pageData.nom || '',
      fournisseur: pageData.fournisseur || '',
      date: pageData.date || '',
      montantHT: pageData.montantHT || '',
      montantTVA: pageData.montantTVA || '',
      montantTTC: pageData.montantTTC || '',
      tauxTVA: pageData.tauxTVA || ''
    });
    setValidationErrors({});
  };

  const handleCancelEdit = () => {
    setEditingPageIndex(null);
    setEditedData({});
    setValidationErrors({});
  };

  const handleFieldChange = (field, value) => {
    setEditedData(prev => ({ ...prev, [field]: value }));
    if (validationErrors[field]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleSaveReceipt = async (pageIndex) => {
    const validation = validateReceiptData(editedData);

    if (!validation.isValid) {
      setValidationErrors(validation.errors);
      showToast('Veuillez corriger les erreurs', 'error');
      return;
    }

    setSavingPageIndex(pageIndex);

    try {
      const normalizedDate = normalizeDate(editedData.date);

      const response = await fetch('/.netlify/functions/saveScannedReceipt', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: JSON.stringify({
          nom: editedData.nom,
          fournisseur: editedData.fournisseur,
          date: normalizedDate,
          montantHT: parseFloat(editedData.montantHT) || 0,
          montantTVA: parseFloat(editedData.montantTVA) || 0,
          montantTTC: parseFloat(editedData.montantTTC) || 0,
          tauxTVA: parseFloat(editedData.tauxTVA) || 20
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Erreur enregistrement');
      }

      const result = await response.json();

      // Mise à jour locale
      const newResults = [...scanResults];
      newResults[pageIndex] = {
        ...newResults[pageIndex],
        data: { ...editedData, date: normalizedDate },
        recordId: result.recordId
      };
      setScanResults(newResults);

      setEditingPageIndex(null);
      setEditedData({});
      setValidationErrors({});

      showToast('Reçu enregistré avec succès', 'success');
    } catch (error) {
      showToast(error.message, 'error');
    } finally {
      setSavingPageIndex(null);
    }
  };

  const handleSaveAll = async () => {
    const validResults = scanResults.filter(r => r.success && r.data && !r.hasValidationErrors);

    if (validResults.length === 0) {
      showToast('Aucun reçu valide à enregistrer', 'warning');
      return;
    }

    setSavingAll(true);

    try {
      let savedCount = 0;
      let errorCount = 0;

      for (let i = 0; i < scanResults.length; i++) {
        const result = scanResults[i];
        if (!result.success || !result.data || result.hasValidationErrors || result.recordId) {
          continue;
        }

        try {
          const normalizedDate = normalizeDate(result.data.date);

          const response = await fetch('/.netlify/functions/saveScannedReceipt', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
            },
            body: JSON.stringify({
              nom: result.data.nom,
              fournisseur: result.data.fournisseur,
              date: normalizedDate,
              montantHT: parseFloat(result.data.montantHT) || 0,
              montantTVA: parseFloat(result.data.montantTVA) || 0,
              montantTTC: parseFloat(result.data.montantTTC) || 0,
              tauxTVA: parseFloat(result.data.tauxTVA) || 20
            })
          });

          if (!response.ok) {
            throw new Error('Erreur enregistrement');
          }

          const saveResult = await response.json();

          // Mise à jour locale
          const newResults = [...scanResults];
          newResults[i] = {
            ...newResults[i],
            recordId: saveResult.recordId
          };
          setScanResults(newResults);

          savedCount++;
        } catch (error) {
          console.error(`Erreur sauvegarde page ${i + 1}:`, error);
          errorCount++;
        }
      }

      if (errorCount === 0) {
        showToast(`${savedCount} reçu${savedCount > 1 ? 's' : ''} enregistré${savedCount > 1 ? 's' : ''} avec succès`, 'success');
      } else {
        showToast(`${savedCount} enregistré${savedCount > 1 ? 's' : ''}, ${errorCount} erreur${errorCount > 1 ? 's' : ''}`, 'warning');
      }
    } catch (error) {
      showToast('Erreur lors de l\'enregistrement', 'error');
    } finally {
      setSavingAll(false);
    }
  };

  const styles = {
    uploadContainer: {
      maxWidth: '1200px',
      margin: '0 auto',
      padding: '0 2rem',
    },
    resultsSection: {
      marginTop: '2rem',
      padding: '0 2rem',
    },
    resultsHeader: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: '0.75rem',
      marginBottom: '1.5rem',
    },
    resultsTitle: {
      margin: 0,
      fontSize: '1.25rem',
      fontWeight: '600',
      color: colors.text.dark,
    },
  };

  return (
    <>
      <div style={styles.uploadContainer}>
        <FileUploadZone
          file={file}
          preview={preview}
          scanning={scanning}
          scanProgress={scanProgress}
          onFileSelect={handleFileSelect}
          onScan={handleScan}
          onReset={handleReset}
        />
      </div>

      {scanResults.length > 0 && (
        <div style={styles.resultsSection}>
          <div style={styles.resultsHeader}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <CheckCircle size={24} style={{ color: colors.status.success }} />
              <h2 style={styles.resultsTitle}>
                Résultats du scan ({scanResults.length} page{scanResults.length > 1 ? 's' : ''})
              </h2>
            </div>

            {scanResults.some(r => r.success && r.data && !r.hasValidationErrors && !r.recordId) && (
              <ActionButton
                icon={Save}
                label={savingAll ? 'Enregistrement...' : 'Tout enregistrer'}
                onClick={handleSaveAll}
                disabled={savingAll}
                variant="primary"
              />
            )}
          </div>

          {scanResults.map((result, index) => (
            <ScanResultCard
              key={index}
              result={result}
              index={index}
              isExpanded={expandedPages[index]}
              isEditing={editingPageIndex === index}
              editedData={editedData}
              validationErrors={validationErrors}
              isSaving={savingPageIndex === index}
              onToggleExpand={() => togglePageExpansion(index)}
              onEdit={() => handleEditPage(index, result.data)}
              onCancelEdit={handleCancelEdit}
              onSave={() => handleSaveReceipt(index)}
              onFieldChange={handleFieldChange}
            />
          ))}
        </div>
      )}
    </>
  );
}

export default ScanTab;
